if (!customElements.get('talli-collection-quick-add')) {
  document.addEventListener('pointerdown', () => {
    document.documentElement.setAttribute('data-quick-add-pointer', '');
  }, true);
  document.addEventListener('keydown', (event) => {
    if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;
    document.documentElement.removeAttribute('data-quick-add-pointer');
  }, true);

  customElements.define(
    'talli-collection-quick-add',
    class TalliCollectionQuickAdd extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
        this.variants = [];
        this.currentVariant = null;
        this.closeAnimationTimer = null;
        this.closePromise = null;
        this.resolveClose = null;
        this.outsideListenerTimer = null;
        this.onDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
        this.onDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
        this.onOptionInteraction = this.handleOptionInteraction.bind(this);
        this.onFormSubmit = this.handleFormSubmit.bind(this);
        this.onCloseTransitionEnd = this.handleCloseTransitionEnd.bind(this);
      }

      connectedCallback() {
        if (this.initialize()) return;

        window.requestAnimationFrame(() => this.initialize());
      }

      initialize() {
        if (this.initialized) return true;

        this.trigger = this.querySelector('[data-collection-quick-add-trigger]');
        this.dialog = this.querySelector('dialog');
        this.inner = this.querySelector('.talli-collection-quick-add__inner');
        this.closeButton = this.querySelector('[data-collection-quick-add-close]');
        this.titleLink = this.querySelector('[data-collection-quick-add-title]');
        this.form = this.querySelector('.talli-collection-quick-add__form');
        this.variantSelect = this.querySelector('[data-collection-quick-add-variant]');
        this.variantData = this.querySelector('[data-collection-quick-add-variants]');
        this.optionGroups = Array.from(this.querySelectorAll('[data-collection-quick-add-option-group]'));
        this.price = this.querySelector('[data-collection-quick-add-price]');
        this.addButton = this.querySelector('.talli-collection-quick-add__add');
        this.addButtonLabel = this.querySelector('[data-collection-quick-add-button-label]');
        this.quantityInput = this.querySelector('.talli-collection-quick-add__quantity');
        this.cardWrapper = this.closest('.card-wrapper');
        this.recommendationsSection = this.closest('product-recommendations');

        if (!this.trigger || !this.dialog || !this.form) return false;

        if (this.variantData?.textContent) {
          try {
            this.variants = JSON.parse(this.variantData.textContent);
          } catch (error) {
            console.error('Unable to read collection quick-add variants.', error);
          }
        }

        this.trigger.addEventListener('click', () => {
          if (this.dialog?.open) {
            this.hide(true);
          } else {
            this.show();
          }
        });
        this.closeButton?.addEventListener('click', (event) => {
          this.closeButton.blur();
          this.hide(event.detail === 0);
        });
        this.titleLink?.addEventListener('click', () => this.hide(false, true));
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.hide();
        });
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.hide(false);
        });
        this.addEventListener('change', this.onOptionInteraction);
        this.addEventListener('input', this.onOptionInteraction);
        this.addEventListener('click', this.onOptionInteraction);
        this.form.addEventListener('submit', this.onFormSubmit, true);

        this.initialized = true;
        this.dataset.collectionQuickAddReady = 'true';
        this.updateVariant();
        return true;
      }

      show() {
        if (!this.initialized && !this.initialize()) return;
        if (this.dialog?.open) return;

        document.querySelectorAll('talli-collection-quick-add').forEach((quickAdd) => {
          if (quickAdd !== this && quickAdd.dialog?.open) quickAdd.hide(false, true);
        });

        this.clearCloseAnimation();
        window.clearTimeout(this.outsideListenerTimer);
        this.dialog.classList.remove('is-closing', 'is-open');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.isMobileDialog = window.matchMedia('(max-width: 749px)').matches;

        if (typeof this.dialog.show === 'function') {
          this.dialog.show();
        } else {
          this.dialog.setAttribute('open', '');
        }

        document.documentElement.classList.add('talli-collection-quick-add-open');
        this.cardWrapper?.classList.add('is-collection-quick-add-open');
        this.recommendationsSection?.classList.add('is-collection-quick-add-open');
        this.updateVariant();
        this.dialog.focus({ preventScroll: true });

        if (!this.isMobileDialog && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          // Give the closed state one frame before transitioning to the open state.
          void this.inner?.offsetHeight;
        }
        this.dialog.classList.add('is-open');
        this.classList.add('is-quick-add-visual-open');

        if (!this.isMobileDialog) {
          this.outsideListenerTimer = window.setTimeout(() => {
            if (!this.dialog?.open || this.isMobileDialog) return;
            document.addEventListener('pointerdown', this.onDocumentPointerDown);
            document.addEventListener('keydown', this.onDocumentKeyDown);
          }, 0);
        }
      }

      hide(restoreFocus = true, immediate = false) {
        if (!this.dialog?.open) return Promise.resolve();
        if (this.dialog.classList.contains('is-closing')) return this.closePromise || Promise.resolve();

        window.clearTimeout(this.outsideListenerTimer);
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        document.removeEventListener('keydown', this.onDocumentKeyDown);
        this.classList.remove('is-quick-add-visual-open');

        const shouldAnimate =
          !immediate &&
          !this.isMobileDialog &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (shouldAnimate) {
          this.closePromise = new Promise((resolve) => {
            this.resolveClose = resolve;
          });
          this.pendingRestoreFocus = restoreFocus;
          this.dialog.classList.remove('is-open');
          this.dialog.classList.add('is-closing');
          this.inner?.addEventListener('transitionend', this.onCloseTransitionEnd);
          this.closeAnimationTimer = window.setTimeout(() => this.finishHide(restoreFocus), 450);
          return this.closePromise;
        }

        this.finishHide(restoreFocus);
        return Promise.resolve();
      }

      handleCloseTransitionEnd(event) {
        if (event.target !== this.inner || event.propertyName !== 'transform') return;
        this.finishHide(this.pendingRestoreFocus);
      }

      clearCloseAnimation() {
        window.clearTimeout(this.closeAnimationTimer);
        this.inner?.removeEventListener('transitionend', this.onCloseTransitionEnd);
        this.closeAnimationTimer = null;
      }

      finishHide(restoreFocus = true) {
        this.clearCloseAnimation();

        if (typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }

        this.dialog.classList.remove('is-closing', 'is-open');
        this.trigger.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('talli-collection-quick-add-open');
        this.cardWrapper?.classList.remove('is-collection-quick-add-open');
        this.recommendationsSection?.classList.remove('is-collection-quick-add-open');
        if (restoreFocus) this.trigger.focus({ preventScroll: true });

        const resolveClose = this.resolveClose;
        this.resolveClose = null;
        this.closePromise = null;
        resolveClose?.();
      }

      handleDocumentPointerDown(event) {
        if (!this.contains(event.target)) this.hide(false);
      }

      handleDocumentKeyDown(event) {
        if (event.key === 'Escape') this.hide();
      }

      handleOptionInteraction(event) {
        if (!event.target.closest?.('[data-collection-quick-add-option]')) return;
        this.updateVariant();
      }

      handleFormSubmit() {
        this.updateVariant();
      }

      updateVariant() {
        if (!this.optionGroups?.length || !this.variants.length) return;

        const selectedValues = [];

        this.optionGroups.forEach((group, groupIndex) => {
          const inputs = Array.from(group.querySelectorAll('[data-collection-quick-add-option]'));

          inputs.forEach((input) => {
            const available = this.variants.some(
              (variant) =>
                variant.available &&
                variant.options[groupIndex] === input.value &&
                selectedValues.every((selectedValue, optionIndex) => variant.options[optionIndex] === selectedValue)
            );

            input.disabled = !available;
            input.nextElementSibling?.classList.toggle('is-unavailable', !available);
          });

          let selectedInput = inputs.find((input) => input.checked && !input.disabled);
          if (!selectedInput) {
            selectedInput = inputs.find((input) => !input.disabled);
            if (selectedInput) selectedInput.checked = true;
          }

          selectedValues.push(selectedInput?.value);
        });

        const selectedVariant = this.variants.find(
          (variant) =>
            variant.options.length === selectedValues.length &&
            variant.options.every((optionValue, optionIndex) => optionValue === selectedValues[optionIndex])
        );

        this.currentVariant = selectedVariant || null;
        const isAvailable = Boolean(selectedVariant?.available);
        const variantOption = selectedVariant
          ? Array.from(this.variantSelect?.options || []).find((option) => option.value === String(selectedVariant.id))
          : null;

        if (this.variantSelect) {
          this.variantSelect.disabled = false;
          Array.from(this.variantSelect.options).forEach((option) => {
            option.selected = option === variantOption;
          });
          if (variantOption) this.variantSelect.value = variantOption.value;
        }

        if (this.price && variantOption?.dataset.price) this.price.textContent = variantOption.dataset.price;

        if (this.addButton) {
          this.addButton.disabled = !isAvailable;
          this.addButton.setAttribute('aria-disabled', String(!isAvailable));
        }

        if (this.addButtonLabel && this.addButton) {
          this.addButtonLabel.textContent = isAvailable
            ? this.addButton.dataset.addLabel
            : this.addButton.dataset.soldOutLabel;
        }

        if (this.quantityInput && selectedVariant?.quantity_rule) {
          const quantityRule = selectedVariant.quantity_rule;
          this.quantityInput.min = quantityRule.min || 1;
          this.quantityInput.step = quantityRule.increment || 1;
          this.quantityInput.value = quantityRule.min || 1;

          if (quantityRule.max) {
            this.quantityInput.max = quantityRule.max;
          } else {
            this.quantityInput.removeAttribute('max');
          }
        }
      }
    }
  );
}

window.addEventListener('pageshow', () => {
  document.querySelectorAll('talli-collection-quick-add').forEach((quickAdd) => {
    if (quickAdd.dialog?.open) quickAdd.finishHide(false);
  });
});
